import { Link } from "react-router-dom"
import { useEffect, useState } from "react"
import { Phone, Mail, Clock, Send, MapPin, Calendar } from "lucide-react"
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import Locationswidget from "../components/widgets/locations"
import SEO from "./SEO"
import api from "../utils/api"
 
export default function Contact() {
  const contactSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad offering expert doctors, advanced diagnostics, and 24/7 emergency care.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ],
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },
 
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaamhospitals.com/contact"
    },
    "headline": "Contact Pranaam Hospitals",
    "description": "Get in touch with Pranaam Hospitals for doctor appointments, emergency medical care, and healthcare enquiries in Hyderabad.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "contact Pranaam Hospitals",
      "hospital contact Hyderabad",
      "emergency hospital Hyderabad",
      "book appointment Pranaam Hospitals"
    ]
  }
];
 
const [faqData, setFaqData] = useState(null)
const [contactData, setContactData] = useState(null)
const [seo, setSeo] = useState({});
const [pageKeywords, setPageKeywords] = useState("");
const [formData, setFormData] = useState({ name: "", phone: "", email: "", patientId: "", subject: "", message: "" });
const [isSubmitting, setIsSubmitting] = useState(false);
const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: string }
 
const handleInputChange = (e) => {
  const { id, value } = e.target;
  setFormData(prev => ({ ...prev, [id]: value }));
};
 
const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setStatus(null);
 
  try {
    const response = await api.post("/wp-json/custom/v1/send-email", formData);
    if (response.data.success) {
      setStatus({ type: "success", message: "Your message has been sent successfully!" });
      setFormData({ name: "", phone: "", email: "", patientId: "", subject: "", message: "" });
    } else {
      setStatus({ type: "error", message: response.data.message || "Failed to send message." });
    }
  } catch (error) {
    setStatus({ type: "error", message: "An error occurred. Please try again later." });
  } finally {
    setIsSubmitting(false);
  }
};
 
useEffect(() => {
  fetch("/wp-json/custom/v1/faqs?page_id=21433")
    .then(res => res.json())
    .then(res => {
      console.log("FAQ 👉", res)
      setFaqData(res)
    })
 
    fetch("/wp-json/custom/v1/contact?page_id=21433")
    .then(res => res.json())
    .then(res => {
      console.log("CONTACT 👉", res)
      setContactData(res)
    })

    fetch("/wp-json/custom/v1/page-seo?slug=contact")
    .then(res => res.json())
    .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
    .catch(() => {})
 
}, [])
 
  return (
    <>
    <SEO
  title={seo.title || "Contact Pranaam Hospitals | Book Appointment & Emergency Care Hyderabad"}
  description={seo.description || "Contact Pranaam Hospitals for appointments, emergency services, and healthcare enquiries. Reach our multi-specialty hospital in Hyderabad for expert medical care."}
  canonical={seo.canonical || "https://pranaamhospitals.com/contact"}
  robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
  keywords={pageKeywords}
  ogTitle={seo.og_title}
  ogDescription={seo.og_description}
  ogImage={seo.og_image || seo.featured_image}
  twitterTitle={seo.twitter_title}
  twitterDescription={seo.twitter_description}
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  schema={contactSchema}
/>
 
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary/90 to-primary py-16 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto ">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              We're Here to Help
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">📞 Contact Us</h1>
            <p className="text-lg opacity-90 mb-4">We're Here to Help You!</p>
            <p className="opacity-90">
              Whether you need medical assistance, have an inquiry, or simply want to give feedback, we're just a call
              or click away. At Pranaam Hospitals, we are committed to providing responsive, compassionate, and expert
              healthcare support.
            </p>
          </div>
        </div>
      </section>
 
      {/* Contact Information and Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6 text-primary">📧 General Inquiries</h2>
                <div className="space-y-4">
                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Phone</h3>
                        {contactData?.phone}
                      </div>
                    </CardContent>
                  </Card>
 
                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Email</h3>
                        {contactData?.email}
                      </div>
                    </CardContent>
                  </Card>
 
                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Clock className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Working Hours</h3>
                        {contactData?.working_hours}
                      </div>
                    </CardContent>
                  </Card>
 
                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Calendar className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Appointments</h3>
                        {contactData?.appointments}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
 
              <div>
                <h2 className="text-2xl font-bold mb-6 text-primary">
                  {contactData?.locations_title || "🏥 Hospital Locations"}
                  </h2>
                <p className="text-gray-600 mb-6">
                  {contactData?.locations_description}</p>
                <Button asChild>
                  <Link to={contactData?.locations_link || "/locations"}>
                    <MapPin className="w-4 h-4 mr-2" />
                    View All Locations
                  </Link>
                </Button>
              </div>
            </div>
 
            {/* Contact Form */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 text-primary">📬 Send Us a Message</h2>
                <p className="text-gray-600 mb-6">
                  If you have a specific question, fill out the form below and our team will get back to you shortly.
                </p>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
 
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
 
                    {/* <div>
                      <label htmlFor="patientId" className="block text-sm font-medium text-gray-700 mb-1">
                        Patient ID
                      </label>
                      <input
                        type="text"
                        id="patientId"
                        name="patientId"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your Patient ID (if any)"
                        value={formData.patientId}
                        onChange={handleInputChange}
                      />
                    </div> */}
 
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
 
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a subject</option>
                        <option value="appointment">Appointment Inquiry</option>
                        <option value="feedback">Feedback</option>
                        <option value="billing">Billing Question</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>
 
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                    </div>
                  </div>
 
                  {status && (
                    <div className={`p-3 rounded-md text-sm ${
                      status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {status.message}
                    </div>
                  )}
 
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    <Send className="h-4 w-4 mr-2" />
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
 
      {/* Branch Locations */}
       <Locationswidget />
 
     
      {/* Map Section */}
      {/* <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-center">Find Us on the Map</h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.4034034234234!2d78.3456789!3d17.4567890!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDI3JzI0LjQiTiA3OMKwMjAnNDQuNiJF!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin%22
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Pranaam Hospitals Location"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section> */}
 
      {/* FAQ Section */}
       <section className="py-12 bg-white">
  <div className="container px-4 mx-auto">
 
    <h2 className="mb-8 text-2xl font-bold text-center md:text-3xl">
      {faqData?.title || "Frequently Asked Questions"}
    </h2>
 
    <div className="max-w-3xl mx-auto space-y-6">
 
      {faqData?.items?.map((faq, index) => (
        <div key={index} className="p-6 bg-white border rounded-lg shadow-sm">
 
          <h3 className="mb-2 text-lg font-bold">
            {faq.question}
          </h3>
 
          <p className="text-muted-foreground">
            {faq.answer}
          </p>
 
        </div>
      ))}
 
    </div>
 
  </div>
</section>
    </main>
  </>
  )
}